// StickyBottomBar.js — Fixed bottom bar with grocery list and save buttons (rendered via portal)
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { getCountryCurrency } from "../../../services/countryService";
import Spinner from "../../UI/Spinner";

const STICKY_ANIMATIONS_ID = "sticky-bottom-bar-animations";
const STICKY_ANIMATIONS_CSS = `
@keyframes savePulseGreen {
  0%, 100% {
    box-shadow:
      0 2px 16px 0 rgba(0, 0, 0, 0.12) inset,
      0 0 32px 0 rgba(255, 255, 255, 0.4) inset,
      0 0 0 2px rgba(0, 153, 0, 0.4),
      0 0 0 0 rgba(0, 200, 83, 0);
  }
  50% {
    box-shadow:
      0 2px 16px 0 rgba(0, 0, 0, 0.12) inset,
      0 0 32px 0 rgba(255, 255, 255, 0.4) inset,
      0 0 0 2px rgba(0, 153, 0, 0.5),
      0 0 16px 4px rgba(0, 200, 83, 0.45);
  }
}
.sbb-save-pulse {
  animation: savePulseGreen 2.4s ease-in-out infinite;
}
.sbb-fade {
  transition: opacity 500ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
}
`;

function ensureStickyAnimationsInjected() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STICKY_ANIMATIONS_ID)) return;
  const style = document.createElement("style");
  style.id = STICKY_ANIMATIONS_ID;
  style.textContent = STICKY_ANIMATIONS_CSS;
  document.head.appendChild(style);
}

/**
 * @param {Object} props
 * @param {boolean} props.canShowControls - Whether the bar should be visible
 * @param {boolean} props.groceryListLoading
 * @param {Array} props.productList
 * @param {string|null} props.adjustedTotalCost
 * @param {string} props.totalCost
 * @param {number} props.computedTotalFromProducts
 * @param {string|null} props.userCountry
 * @param {boolean} props.isSavingMealPlan
 * @param {Function} props.onOpenGroceryList
 * @param {Function} props.onSaveMealPlan
 * @param {boolean} [props.introActive] - When true, bar stays off-screen below the viewport
 */
function StickyBottomBar({
  canShowControls,
  groceryListLoading,
  productList,
  adjustedTotalCost,
  totalCost,
  computedTotalFromProducts,
  userCountry,
  isSavingMealPlan,
  onOpenGroceryList,
  onSaveMealPlan,
  introActive = false,
}) {
  const { t } = useTranslation('mealplan');

  useEffect(() => {
    ensureStickyAnimationsInjected();
  }, []);

  // Slide-in on mount: always start hidden below viewport, flip to visible next frame
  // unless introActive is true (then wait for intro collapse).
  const [readyToShow, setReadyToShow] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setReadyToShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const offScreen = introActive || !readyToShow;

  const isGroceryDisabled = groceryListLoading || productList.length === 0;
  const isSaveDisabled = isGroceryDisabled || isSavingMealPlan;
  const showLoading = !canShowControls;

  const currencySymbol = userCountry ? getCountryCurrency(userCountry).symbol : "€";
  const costValue = adjustedTotalCost || totalCost;
  let numericValue = 0;
  if (costValue) {
    numericValue = Math.floor(parseFloat(costValue.replace(/[^0-9.]+/g, ""))) || 0;
  }
  if (numericValue === 0 && computedTotalFromProducts > 0) {
    numericValue = Math.floor(computedTotalFromProducts);
  }
  const costBadge = numericValue > 0 ? `${numericValue}${currencySymbol}` : `0${currencySymbol}`;

  return createPortal(
    <div
      className={`
        fixed left-[20px] right-[20px] bottom-[16px]
        py-2 px-2 pl-2
        flex justify-center
        bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
        backdrop-blur-[64px]
        z-50
        mx-auto
        max-w-5xl
        rounded-[96px]
        border-[1px] border-[#fff]
        shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
      `}
      style={{
        transform: offScreen
          ? 'translate3d(0, calc(100% + 32px), 0)'
          : 'translate3d(0, 0, 0)',
        transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'transform',
        pointerEvents: offScreen ? 'none' : 'auto',
      }}
    >
      <div className="relative w-full max-w-5xl h-[48px]">
        {/* Loading State */}
        <div
          className={`sbb-fade absolute inset-0 flex items-center justify-center gap-2 pl-4 pr-2 ${
            showLoading
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={!showLoading}
        >
          <span className="text-[#0D0C07] text-[14px] font-medium leading-[20px]">
            {t('loading.mealSetLoading')}
          </span>
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <DotLottieReact
              src="/assets/recipe-preloader.lottie"
              loop
              autoplay
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>

        {/* Ready State (buttons) */}
        <div
          className={`sbb-fade absolute inset-0 gap-4 flex items-center justify-between ${
            showLoading
              ? "opacity-0 pointer-events-none"
              : "opacity-100 pointer-events-auto"
          }`}
          aria-hidden={showLoading}
        >
          {/* Grocery List Button */}
          <button
            className={`
              relative
              flex flex-col items-center gap-0 justify-center
              h-[48px] pt-[4px]
              rounded-[32px]
              w-[132px]
              text-[#0D0C07]
              text-[12px] font-normal leading-[20px]
              transition-colors
              ${isGroceryDisabled
                ? "cursor-not-allowed opacity-60"
                : "md:hover:bg-[rgba(255,255,255,0.3)]"
              }
            `}
            onClick={isGroceryDisabled ? undefined : onOpenGroceryList}
            disabled={isGroceryDisabled}
            tabIndex={showLoading ? -1 : 0}
          >
            {!groceryListLoading && (
              <span className="absolute bg-[#FFCB00] px-1 rounded-[6px] right-[15%] top-[2px]">
                {costBadge}
              </span>
            )}
            {groceryListLoading ? (
              <Spinner size="md" color="#0D0C07" className="mb-1" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
              >
                <path
                  d="M15.5 11L14.5 20M19.5 11L15.5 4M2.5 11H22.5M4 11L5.6 18.4C5.6935 18.8586 5.94485 19.2698 6.31028 19.5621C6.67572 19.8545 7.13211 20.0094 7.6 20H17.4C17.8679 20.0094 18.3243 19.8545 18.6897 19.5621C19.0552 19.2698 19.3065 18.8586 19.4 18.4L21.1 11M5 15.5H20M5.5 11L9.5 4M9.5 11L10.5 20"
                  stroke="#0D0C07"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span>{t('grocery.groceriesTab')}</span>
          </button>

          {/* Save Button */}
          <button
            className={`
              inline-flex justify-center items-center gap-1
              h-[48px] px-[24px] py-[14px]
              rounded-[32px]
              border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              text-[#FFF] text-center
              text-[14px] font-medium leading-[28px]
              transition-colors
              w-full
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
              ${isSaveDisabled
                ? "cursor-not-allowed opacity-60 bg-[#090]"
                : "cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060] sbb-save-pulse"
              }
            `}
            onClick={isSaveDisabled ? undefined : onSaveMealPlan}
            disabled={isSaveDisabled}
            tabIndex={showLoading ? -1 : 0}
          >
            {isSavingMealPlan ? (
              <>
                <Spinner size="sm" color="#FFF" />
                <span>{t('actions.saving')}</span>
              </>
            ) : (
              <span>{t('actions.save')}</span>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default StickyBottomBar;
